// Helpers around the admin-editable equipment catalog. The catalog
// itself ships down from /api/config (registry key `equipment_catalog`)
// and lives on useConfig().equipment. This module exposes safe
// readers so a missing catalog or a missing kind doesn't crash the
// picker — every accessor returns a sensible "Other equipment"
// shaped fallback.
//
// The legacy export names (EQUIPMENT_SECTIONS, KIND_META, KIND_BRANDS,
// KIND_SUBTYPES, kindMeta) are kept as compatibility wrappers that
// take a catalog and return what the old constants used to. New code
// should call the helpers directly with the catalog from useConfig().

const FALLBACK_KIND = {
  label: 'Other equipment',
  icon: '🔧',
  brands: [],
  subtypes: [],
  parts: [],
};

export function getSections(catalog) {
  return Array.isArray(catalog?.sections) ? catalog.sections : [];
}

export function getKind(catalog, kind) {
  return catalog?.kinds?.[kind] || null;
}

export function kindMeta(catalog, kind) {
  const k = getKind(catalog, kind);
  if (!k) return FALLBACK_KIND;
  return {
    label: k.label || 'Equipment',
    icon: k.icon || '🔧',
    integration: k.integration || null,
    brands: Array.isArray(k.brands) ? k.brands : [],
    subtypes: Array.isArray(k.subtypes) ? k.subtypes : [],
    parts: Array.isArray(k.parts) ? k.parts : [],
  };
}

export function getBrandsForKind(catalog, kind) {
  return kindMeta(catalog, kind).brands;
}

export function getSubtypesForKind(catalog, kind) {
  return kindMeta(catalog, kind).subtypes;
}

export function getPartKinds(catalog) {
  return catalog?.part_kinds || {};
}

export function partKindLabel(catalog, partKind) {
  return catalog?.part_kinds?.[partKind]?.label || partKind || 'Part';
}

// The catalog's usual parts for this kind (an RO/DI's cartridges and
// membrane) that are not on the card yet, so the usual ones are one tap.
export function unTrackedSuggestions(suggested, parts) {
  const have = new Set();
  for (const p of parts || []) {
    if (p.kind && p.kind !== 'custom') have.add(`k:${p.kind}`);
    if (p.label) have.add(`l:${p.label.trim().toLowerCase()}`);
  }
  return (suggested || []).filter(
    (s) => s?.label && !have.has(`k:${s.kind}`) && !have.has(`l:${s.label.trim().toLowerCase()}`)
  );
}

export function catalogPartByName(catalogParts, name) {
  const want = String(name || '')
    .trim()
    .toLowerCase();
  if (!want) return null;
  return (catalogParts || []).find((s) => s?.label?.trim().toLowerCase() === want) || null;
}

// What Add part sends. A usual part, tapped or typed by its name, keeps its
// catalog kind (the reorder library is keyed on it) and service cadence, as
// the add-equipment flow saves them; anything else is the keeper's own.
export function newPartBody({ label, days, link, catalogParts, brand, today }) {
  const name = String(label || '').trim();
  const fromCatalog = catalogPartByName(catalogParts, name);
  return {
    kind: fromCatalog?.kind || 'custom',
    label: fromCatalog?.label || name,
    ...(fromCatalog ? { brand: brand?.trim() || null } : {}),
    installed_at: today,
    replace_every_days: Number(days) || null,
    reorder_url: link || fromCatalog?.reorder_url || null,
    ...(fromCatalog?.service_every_days
      ? {
          service_every_days: fromCatalog.service_every_days,
          service_label: fromCatalog.service_label || null,
        }
      : {}),
  };
}

// A part's dates are calendar days, but postgres.js sends a DATE as midnight
// UTC: read as a moment, a cartridge put in today read "20h ago" and "Due in
// 89d" west of UTC. Whole days between that date and the keeper's today; a
// real moment (any other time of day) counts from its own local date.
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})(T00:00:00(\.0+)?Z)?$/;
export function daysSinceCalendarDay(v, now = new Date()) {
  if (!v) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const m = DATE_ONLY.exec(String(v));
  let day;
  if (m) day = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  else {
    const t = new Date(v);
    if (Number.isNaN(t.getTime())) return null;
    day = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }
  return Math.round((today - day) / 86400000);
}

export function calendarDayAgo(v, now = new Date()) {
  const n = daysSinceCalendarDay(v, now);
  if (n == null) return null;
  if (n <= 0) return 'today';
  if (n === 1) return 'yesterday';
  if (n < 30) return `${n}d ago`;
  const m = DATE_ONLY.exec(String(v));
  const date = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  // A membrane put in 14 months ago must not read as this year's date.
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

// Status pill computed from the part's installed/last_replaced date
// + replace cadence. Home's rows (utils.js runwayBucket) read the same day
// the same way: "Due today" on both, never "Past due" on one.
export function cadenceStatus(everyDays, anchor) {
  if (!everyDays) return { kind: 'untracked', label: 'No interval' };
  if (!anchor) return { kind: 'untracked', label: 'No date' };
  const elapsed = Math.max(0, daysSinceCalendarDay(anchor) ?? 0);
  const remaining = everyDays - elapsed;
  if (remaining < 0) return { kind: 'critical', label: `Past due ${-remaining}d` };
  if (remaining === 0) return { kind: 'critical', label: 'Due today' };
  if (remaining <= 7) return { kind: 'critical', label: `Due in ${remaining}d` };
  if (remaining <= 30) return { kind: 'low', label: `Due in ${remaining}d` };
  return { kind: 'ok', label: `Due in ${remaining}d` };
}

export function partStatus(part) {
  return cadenceStatus(part.replace_every_days, part.last_replaced_at || part.installed_at);
}

// Service (maintenance) status — independent of the replace schedule.
export function serviceStatus(part) {
  if (!part.service_every_days) return null;
  return cadenceStatus(part.service_every_days, part.last_serviced_at || part.installed_at);
}

// Pick the worst-status part on an equipment row so the parent card
// can show one summary pill before the user expands it.
export function rollupStatus(parts) {
  if (!parts?.length) return { kind: 'none', label: 'No parts tracked' };
  let best = { kind: 'ok', label: 'On track' };
  const order = { critical: 0, low: 1, ok: 2, untracked: 3, none: 4 };
  // A service due counts too: a skimmer read "On track" closed while its cup
  // was due inside (review, 2026-10-24).
  for (const p of parts) {
    for (const s of [partStatus(p), serviceStatus(p)]) {
      if (s && order[s.kind] < order[best.kind]) best = s;
    }
  }
  return best;
}

// What a retry changed since the first save the server kept: the interval,
// or a link the keeper added. Null when nothing did.
export function retryChanges(saved, body) {
  const out = {};
  if ((saved.replace_every_days ?? null) !== (body.replace_every_days ?? null))
    out.replace_every_days = body.replace_every_days ?? null;
  if (body.reorder_url && saved.reorder_url !== body.reorder_url)
    out.reorder_url = body.reorder_url;
  return Object.keys(out).length ? out : null;
}
